<script setup lang="ts">
/**
 * Page de recherche des chambres d'hôtes
 *
 * Affiche une liste d'établissements avec filtres et pagination.
 * L'état de recherche est préservé dans l'URL (query params).
 */

import { LARGE_GROUP_THRESHOLD, MAP_CONFIG, parseAccommodationTypeParam } from '~~/shared/search-constants'
import type { MapBounds } from '~~/shared/search-constants'
import { getTagline } from '~~/shared/region-tagline-map'
import { formatSejoursParam } from '~~/shared/establishment-filters'
import { REGIONS, type RegionData } from '~~/shared/regions'
import { getMapContext, getZoomUpTarget } from '~/composables/useMapHierarchy'
import { criteriaNavigationQuery, guestsFromFilters, pseoTypeFromFilters } from '~/utils/pseo-guest-query'
import { livingSettingQuery } from '~~/shared/living-setting'
import { panelCriteriaCount } from '~/composables/useSearchFiltersState'
import { withSearchType, type SearchTypeValue } from '~/utils/search-bar-ui'

const { t } = useI18n()
const localePath = useLocalePath()

definePageMeta({
  layout: 'default',
  keepalive: true, // Préserver l'état lors de la navigation
  name: 'search', // Nom explicite pour le keepalive matching
})

// SEO
const config = useRuntimeConfig()
const { locale } = useI18n()
const ogLocale = useOgLocale()
const localePrefix = computed(() => locale.value === 'fr' ? '' : `/${locale.value}`)

// --- Robots meta ---
// La page de recherche est une facette infinie : chaque combinaison
// centerLat/centerLng/radiusKm/locationName produit une URL distincte alors que
// le title, le h1 et la structure restent identiques. Google indexait ces
// variantes puis les reclassait en « soft 404 » — 4 226 pages au 24/07/2026,
// soit 100 % des soft 404 du site (export GSC Coverage-Drilldown 2026-08-01).
//
// Le canonical vers `/search` ne suffit pas : Google le voit mais juge quand
// même la page vide de valeur ajoutée. On coupe donc à la source dès qu'un
// query param est présent. `follow` est conservé pour que les liens vers les
// fiches restent crawlés.
//
// Volontairement PAS de `Disallow: /search` dans robots.txt : bloquer le crawl
// empêcherait Google de voir le canonical et le noindex, et les URL déjà
// connues resteraient indéfiniment en « indexée malgré le blocage ».
//
// On n'émet la balise QUE sur la variante à paramètres : `useHead` écrase la
// balise robots de nuxt-robots, y compris son garde-fou `noindex, nofollow`
// hors production. Laisser le module seul maître de `/search` nu évite de
// rendre la page indexable sur un environnement de preview.
const searchRoute = useRoute()
const hasSearchParams = computed(() => Object.keys(searchRoute.query).length > 0)

useHead(() => ({
  meta: hasSearchParams.value
    ? [{ name: 'robots', content: 'noindex, follow' }]
    : [],
}))

useSeoMeta({
  title: () => t('pages.search.seo.title'),
  description: () => t('pages.search.seo.description'),
  ogTitle: () => t('pages.search.seo.ogTitle'),
  ogDescription: () => t('pages.search.seo.ogDescription'),
  ogImage: `${config.public.siteUrl}/images/og-default.jpg`,
  ogImageType: 'image/jpeg',
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogType: 'website',
  ogSiteName: 'Holicosy',
  ogUrl: () => `${config.public.siteUrl}${localePrefix.value}/search`,
  ogLocale: ogLocale,
})

// Breadcrumb
const breadcrumbItems = computed(() => [
  { label: t('common.breadcrumb.home'), to: localePath('/') },
  { label: t('common.breadcrumb.search') },
])

useSchemaOrg([
  defineWebPage({
    '@type': 'SearchResultsPage',
    'name': t('pages.search.seo.title'),
    'description': t('pages.search.seo.description'),
  }),
  {
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Holicosy', 'item': config.public.siteUrl },
      { '@type': 'ListItem', 'position': 2, 'name': t('common.breadcrumb.search'), 'item': `${config.public.siteUrl}/search` },
    ],
  },
])

// Utiliser le composable centralisé pour la recherche
const {
  filters,
  establishments,
  mapPoints,
  total,
  hasMore,
  loading,
  error,
  expandedRadiusKm,
  isLoadMoreInFlight,
  newResultsAdded,
  viewportSyncMode,
  displayedEstablishments,
  viewportStats,
  shouldSuggestRefresh,
  updateFilters,
  resetFilters,
  search,
  loadMore,
  hydrateFromQuery,
  syncToViewport,
  syncToCurrentViewport,
  resetViewportSync,
  updateCurrentViewport,
} = useSearch()

// ===== Helpers locaux admin (région/département) =====
// Remontés avant la logique viewport sync qui en a besoin pour clusterContextLabels.
const REGION_BY_SLUG = new Map(REGIONS.map(r => [r.slug, r]))
// `filters.region` peut être un slug (ex: 'bretagne') OU un nom complet ('Bretagne'),
// selon la source d'arrivée (page pSEO envoie le nom car l'API attend le nom).
const REGION_BY_NAME_LOWER = new Map(REGIONS.map(r => [r.name.toLowerCase(), r]))
function resolveRegion(value: string | undefined): RegionData | null {
  if (!value) return null
  return REGION_BY_SLUG.get(value) ?? REGION_BY_NAME_LOWER.get(value.toLowerCase()) ?? null
}

// Department helper (client-side reimplémentation simple car server/utils/departments
// n'est pas accessible côté client — à factoriser ultérieurement si besoin)
const DEPT_NAMES: Record<string, { name: string; regionName: string; regionSlug: string }> = {}
for (const region of REGIONS) {
  for (const dept of region.departments) {
    DEPT_NAMES[dept.code] = { name: dept.name, regionName: region.name, regionSlug: region.slug }
  }
}

// Sync viewport ↔ liste (Pattern C, desktop only). Voir spec
// _bmad-output/implementation-artifacts/spec-search-map-list-viewport-sync.md

// Vrai entre un emit `cluster-click` et le `viewport-change` suivant : permet
// de distinguer un viewport-change post-cluster (intent → sync) d'un pan manuel.
let _syncOnNextViewport = false

// Si défini, le prochain viewport-change adopte ce contexte (au lieu de getMapContext)
// — utile pour les dézooms hiérarchiques où on connaît déjà la cible exacte
// (ex: ↑ Occitanie → on sait qu'on remonte sur Occitanie, pas besoin de redeviner via bbox).
let _pendingClusterContextOverride: ReturnType<typeof getMapContext> | null = null

function handleClusterClick() {
  _syncOnNextViewport = true
}

// Dernière bbox reçue de la carte (sert au dézoom hiérarchique + Rechercher ici)
const _currentBbox = ref<MapBounds | null>(null)

/**
 * Contexte explorée résolu **uniquement au clic cluster** (intent fort).
 * Sert à afficher un sous-titre éditorial "Vue actuelle : Aveyron · Occitanie"
 * sans polluer le H1 SEO ni flicker au pan/zoom. Reset au clic "Voir tous"
 * / dézoom hiérarchique / changement filtres serveur.
 */
const clusterContext = ref<ReturnType<typeof getMapContext> | null>(null)

function handleViewportChange(bbox: MapBounds) {
  _currentBbox.value = bbox
  updateCurrentViewport(bbox)
  if (_syncOnNextViewport) {
    _syncOnNextViewport = false
    syncToViewport(bbox)
    // Résout le contexte géographique une seule fois, à l'instant du clic cluster.
    // Pas de re-calcul sur pan/zoom ultérieur — évite le flicker du sous-titre.
    clusterContext.value = getMapContext(bbox)
  }
  else if (_pendingClusterContextOverride !== null) {
    // Dézoom hiérarchique : on connaît déjà la cible (Occitanie / France) et on
    // adopte directement ce contexte — plus fiable que getMapContext(bbox) qui peut
    // basculer en 'ambiguous' à cause des marges padding du fitBounds.
    clusterContext.value = _pendingClusterContextOverride
    _pendingClusterContextOverride = null
  }
}

/** Labels dérivés pour le sous-titre "Vue actuelle : <dept> · <région>" (uppercase mono). */
const clusterContextLabels = computed<{ dept?: string, region?: string } | null>(() => {
  const ctx = clusterContext.value
  if (!ctx) return null
  if (ctx.level === 'department' && ctx.departmentCode) {
    const dept = DEPT_NAMES[ctx.departmentCode]
    if (dept) return { dept: dept.name, region: dept.regionName }
  }
  if (ctx.level === 'region' && ctx.regionSlug) {
    const region = REGION_BY_SLUG.get(ctx.regionSlug)
    if (region) return { region: region.name }
  }
  return null
})

// Clear clusterContext dès que le mode synced se désactive (auto-reset via watcher
// du composable sur changement filtres serveur, ou reset explicite côté UI).
// Exception : si un override hiérarchique est en attente (handleZoomUp), on garde
// l'ancien le temps que le viewport-change arrive et l'écrase avec la nouvelle valeur.
watch(viewportSyncMode, (mode) => {
  if (mode === 'off' && _pendingClusterContextOverride === null) {
    clusterContext.value = null
  }
})

// Divergence différée (VIEWPORT_REFRESH_DEBOUNCE_MS) pour éviter le clignotement
// du bouton "Rechercher dans cette zone" pendant un pan continu.
const showSearchInAreaButton = ref(false)
let _showButtonTimer: ReturnType<typeof setTimeout> | null = null

watch(shouldSuggestRefresh, (suggest) => {
  if (_showButtonTimer) {
    clearTimeout(_showButtonTimer)
    _showButtonTimer = null
  }
  if (suggest) {
    _showButtonTimer = setTimeout(() => {
      showSearchInAreaButton.value = true
      _showButtonTimer = null
    }, MAP_CONFIG.VIEWPORT_REFRESH_DEBOUNCE_MS)
  }
  else {
    showSearchInAreaButton.value = false
  }
})

onUnmounted(() => {
  if (_showButtonTimer) clearTimeout(_showButtonTimer)
})

function handleSearchInThisArea() {
  syncToCurrentViewport()
  showSearchInAreaButton.value = false
}

// Ref sur la carte desktop (exposée : zoomToFrance + flyToBbox pour dézoom hiérarchique)
const desktopMapRef = ref<{ zoomToFrance: () => void, flyToBbox: (bbox: MapBounds) => void } | null>(null)

/** Reset complet : liste revient à `establishments` paginé + carte dézoome France. */
function handleResetViewportSync() {
  resetViewportSync()
  desktopMapRef.value?.zoomToFrance()
  clusterContext.value = null
}

// Dézoom hiérarchique : calcule le label + bbox cible depuis la viewport courante
const _zoomUpTarget = computed(() => {
  if (!_currentBbox.value) return null
  const ctx = getMapContext(_currentBbox.value)
  return getZoomUpTarget(ctx)
})

const zoomUpLabel = computed<string | null>(() => _zoomUpTarget.value?.label ?? null)

function handleZoomUp() {
  const target = _zoomUpTarget.value
  if (!target) return
  // Pose l'override AVANT le flyToBbox : le viewport-change post-animation l'adoptera.
  // - target.level === 'region' → sous-titre "VUE ACTUELLE · OCCITANIE"
  // - target.level === 'france' → label vide → sous-titre disparaît proprement
  _pendingClusterContextOverride = target.level === 'region'
    ? { level: 'region', regionSlug: getRegionSlugFromCurrentContext() }
    : { level: 'france' }
  desktopMapRef.value?.flyToBbox(target.bbox)
  // Si on était en mode synced, le reset auto sur viewport-change ne suffira pas
  // (on change juste la carte, pas les filtres serveur). On reset explicitement
  // pour que la liste reflète à nouveau tous les résultats globaux.
  if (viewportSyncMode.value === 'synced') {
    resetViewportSync()
  }
}

/** Récupère le regionSlug depuis le clusterContext courant (ou null si on était au niveau France). */
function getRegionSlugFromCurrentContext(): string | undefined {
  return clusterContext.value?.regionSlug
}

// Effectuer la recherche initiale — hydrate si l'URL contient ?page=N>1 (F5, lien partagé)
const _initialPage = Number(useRoute().query.page) || 1
if (_initialPage > 1) {
  const { capped } = await hydrateFromQuery(_initialPage)
  if (capped && import.meta.client) {
    useToast().add({ title: t('pages.search.tooManyPages'), color: 'warning' })
  }
}
else {
  await search()
}

// Event produit : arrivée sur une page de résultats (couvre TOUTE recherche aboutie,
// quelle que soit la source : hero home, lien partagé, F5, affinage de filtres).
// Complète search_performed (qui ne couvre que le lancement depuis le hero).
const { capture: capturePostHog } = usePostHog()
if (import.meta.client) {
  capturePostHog('search_results_viewed', {
    location_type: filters.value.department
      ? 'department'
      : filters.value.region
        ? 'region'
        : filters.value.city
          ? 'city'
          : filters.value.q
            ? 'freetext'
            : 'none',
    has_dates: !!(filters.value.checkIn && filters.value.checkOut),
    adults: filters.value.minAdults ?? null,
    results_count: total.value,
  })
}

// Watcher pour re-chercher quand les filtres changent (sauf page - géré par loadMore / popstate)
// Debounce pour éviter les requêtes multiples lors de changements rapides.
// Le flag isLoadMoreInFlight empêche les re-fires quand seule `page` change.
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined
watch(
  () => {
    const { page: _page, ...filtersWithoutPage } = filters.value
    return JSON.stringify(filtersWithoutPage)
  },
  (newVal, oldVal) => {
    if (newVal === oldVal) return
    if (isLoadMoreInFlight.value) return
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      search()
    }, 150)
  },
)

// A11y : annonce les nouveaux résultats + focus programmatique sur la 1re nouvelle card
const newResultsAnnouncement = ref('')
watch(newResultsAdded, (info) => {
  if (!info || !import.meta.client) return
  newResultsAnnouncement.value = t('pages.search.newResultsAnnounced', { count: info.count })
  nextTick(() => {
    const cards = document.querySelectorAll<HTMLElement>('[data-establishment-id]')
    const firstNew = cards[info.startIndex]
    if (firstNew) {
      firstNew.setAttribute('tabindex', '-1')
      firstNew.focus({ preventScroll: true })
    }
  })
})

onUnmounted(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})

// Gestion du hover pour synchronisation carte
const hoveredEstablishmentId = ref<string | null>(null)

// ID de l'établissement sélectionné (popup carte ouvert)
const selectedEstablishmentId = ref<string | null>(null)

// ID combiné pour le highlight des cards (sélection prioritaire sur hover)
const highlightedEstablishmentId = computed(() =>
  selectedEstablishmentId.value || hoveredEstablishmentId.value,
)

// Toggle carte mobile
const showMobileMap = ref(false)

// Toggle filtres mobile (piloté depuis la bottom bar)
// Auto-open if navigated with ?openFilters=1 (e.g. from room detail page)
const route = useRoute()
const showMobileFilters = ref(route.query.openFilters === '1')

// React to client-side navigation (e.g. from room detail with ?openFilters=1)
watch(() => route.query.openFilters, (val) => {
  if (val === '1') {
    showMobileFilters.value = true
  }
})

// Badge « Filtres (n) » de la barre basse : critères que regroupe le tiroir
// (envies, prix, rayon), comptés comme le badge desktop et « Réinitialiser (n) ».
const activeFilterCount = computed(() => panelCriteriaCount(filters.value))

// Derive display label from active location filters
function getMobileLocationLabel(): string {
  if (filters.value.q) return filters.value.q
  if (filters.value.city) return filters.value.city
  if (filters.value.region) return filters.value.region
  return ''
}

// SearchText local pour la bottom bar mobile — deferred apply (pas d'auto-recherche)
const _mobileDeptLabel = ref((route.query.departmentName as string) || '')
const mobileSearchText = ref(getMobileLocationLabel() || _mobileDeptLabel.value)
let _mobileIsSyncing = false

// Pending location for bottom bar (same pattern as SearchFilters)
const _mobilePendingLocation = ref<{ type: string; value: string; code?: string; slug?: string; lat?: number; lng?: number } | null>(null)

// Sync filters → mobileSearchText (one-way: when filters change externally, update display)
watch(() => [filters.value.q, filters.value.city, filters.value.region, filters.value.department] as const, () => {
  if (_mobilePendingLocation.value) return
  _mobileIsSyncing = true
  const label = getMobileLocationLabel()
  mobileSearchText.value = label || _mobileDeptLabel.value || ''
  nextTick(() => { _mobileIsSyncing = false })
})

// mobileSearchText typing → store as pending freetext (NO auto-search)
watch(mobileSearchText, (val) => {
  if (_mobileIsSyncing) return
  _mobileDeptLabel.value = ''
  _mobilePendingLocation.value = { type: 'freetext', value: val }
})

// Suggestion selected in bottom bar → store locally, no search
function handleMobileSuggestionSelect(suggestion: { type: string; value: string; code?: string; slug?: string }) {
  _mobileIsSyncing = true
  _mobilePendingLocation.value = suggestion
  nextTick(() => { _mobileIsSyncing = false })
}

// Merge pending mobile location into filters and return new filters object
function mergeMobilePending(): typeof filters.value | null {
  if (!_mobilePendingLocation.value) return null
  const loc = _mobilePendingLocation.value
  const { city: _c, region: _r, department: _d, q: _q, centerLat: _clat, centerLng: _clng, ...rest } = filters.value
  const update: Record<string, unknown> = { ...rest, page: 1 }
  if (loc.type === 'city') update.city = loc.value
  else if (loc.type === 'region') update.region = loc.value
  else if (loc.type === 'department' && loc.code) {
    update.department = loc.code
    _mobileDeptLabel.value = loc.value
  }
  else if (loc.type === 'tourist_site' && loc.lat && loc.lng) {
    update.centerLat = loc.lat
    update.centerLng = loc.lng
  }
  else if (loc.type === 'freetext' && loc.value) update.q = loc.value
  _mobilePendingLocation.value = null
  return update as typeof filters.value
}

// Apply pending mobile location (called by "Rechercher" button)
// Navigate to PSEO pages for department/region selections
function applyMobileSearch() {
  const loc = _mobilePendingLocation.value
  if (loc) {
    // Département avec slug → page PSEO /{code}-{slug}
    if (loc.type === 'department' && loc.code && loc.slug) {
      _mobilePendingLocation.value = null
      navigateTo({ path: localePath(`/${loc.code}-${loc.slug}`), query: pseoNavigationQuery(loc.value) })
      return
    }
    // Région avec slug → page PSEO /regions/{slug}
    if (loc.type === 'region' && loc.slug) {
      _mobilePendingLocation.value = null
      navigateTo({ path: localePath(`/regions/${loc.slug}`), query: pseoNavigationQuery(loc.value) })
      return
    }
  }
  const merged = mergeMobilePending()
  if (merged) updateFilters(merged)
}

// Query vers une page département/région : type (Tout explicite, défaut de ces
// pages = chambres d'hôtes), dates, voyageurs et séjours types des filtres sous les noms que ces
// pages lisent (`minAdults`/`minChildren`, pas `adults`/`children` des fiches).
function pseoNavigationQuery(locationName: string): Record<string, string> {
  const { checkIn, checkOut } = filters.value
  return criteriaNavigationQuery('pseo', {
    type: pseoTypeFromFilters(filters.value.type),
    dates: checkIn && checkOut ? { start: checkIn, end: checkOut } : null,
    guests: guestsFromFilters(filters.value),
    sejours: filters.value.sejours,
    living: filters.value,
    extra: { locationName },
  })
}

// Open mobile filters drawer — apply pending location first so the drawer input shows it
function openMobileFilters() {
  const merged = mergeMobilePending()
  if (merged) updateFilters(merged)
  showMobileFilters.value = true
}

// Label lisible du département courant (ex: "Aveyron" pour department=12)
// Alimenté par l'emit locationLabel de HoliSearchFilters
const _currentDeptLabel = ref((route.query.departmentName as string) || '')

// Recherche « grand groupe » (spec 4.107.0) : au-delà du seuil, un résultat vide
// n'est pas un écran muet — on nomme l'effectif demandé et on oriente vers les gîtes,
// seuls hébergements loués en entier. L'URL n'est PAS basculée d'office sur
// `type=gite` : elle doit continuer de refléter ce que l'utilisateur a demandé.
const requestedGuestTotal = computed(
  () => (filters.value.minAdults ?? 0) + (filters.value.minChildren ?? 0),
)
const isLargeGroupSearch = computed(
  () => requestedGuestTotal.value >= LARGE_GROUP_THRESHOLD,
)
const canSuggestGites = computed(() => parseAccommodationTypeParam(filters.value.type) !== 'gite')

/** CTA « voir les gîtes » de l'état vide grand groupe : restreint le type, garde le reste. */
function switchToGites() {
  updateFilters({ ...filters.value, type: 'gite', page: 1 })
}

function buildSearchQuery(extra?: Record<string, string>): Record<string, string> {
  const query: Record<string, string> = { ...extra }
  if (filters.value.checkIn) query.checkIn = filters.value.checkIn
  if (filters.value.checkOut) query.checkOut = filters.value.checkOut
  if (filters.value.minAdults != null) query.adults = String(filters.value.minAdults)
  if (filters.value.minChildren != null && filters.value.minChildren > 0) query.children = String(filters.value.minChildren)
  if (filters.value.childrenAges && filters.value.childrenAges.length > 0) query.childrenAges = filters.value.childrenAges.join(',')
  // Type d'hébergement : porté par l'URL de la fiche pour que « Revenir aux
  // résultats » restitue le même périmètre. `all` reste une URL sans paramètre.
  const accommodationType = parseAccommodationTypeParam(filters.value.type)
  if (accommodationType) query.type = accommodationType
  // Séjours types : restitués par « Revenir aux résultats » (repli /search).
  const sejours = formatSejoursParam(filters.value.sejours)
  if (sejours) query.sejours = sejours
  // Cadre de vie : idem.
  Object.assign(query, livingSettingQuery(filters.value))
  // Filtres de localisation pour permettre le retour à la recherche
  if (filters.value.q) query.q = filters.value.q
  if (filters.value.city) query.city = filters.value.city
  if (filters.value.region) query.region = filters.value.region
  if (filters.value.department) {
    query.department = filters.value.department
    // Inclure le nom lisible pour l'affichage sur la fiche établissement
    if (_currentDeptLabel.value) query.departmentName = _currentDeptLabel.value
  }
  return query
}

// Navigation depuis popup carte (mobile) : fermer le drawer puis naviguer
function handlePopupNavigate(path: string) {
  showMobileMap.value = false
  navigateTo({ path, query: buildSearchQuery({ from: 'map' }) })
}

// Scroll vers une card depuis un clic sur marqueur carte
function scrollToCard(id: string | null) {
  selectedEstablishmentId.value = id
  if (!id) return
  const card = document.querySelector(`[data-establishment-id="${id}"]`)
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// Préservation du scroll position avec keepalive
const { resetScrollPosition } = useScrollRestore('search-page')

// Rouvrir la carte mobile au retour si demandé + réactiver bottom bar
const reopenMapOnBack = useState<boolean>('reopen-map-on-back', () => false)
const mapSavedViewport = useState<{ lat: number, lng: number, zoom: number } | null>('map-saved-viewport', () => null)

function clearSavedViewport() {
  mapSavedViewport.value = null
}

onActivated(() => {
  if (reopenMapOnBack.value) {
    reopenMapOnBack.value = false
    showMobileMap.value = true
  }
})

// Réinitialiser le scroll quand on reset les filtres
watch(
  () => Object.keys(filters.value).length,
  (newLen, oldLen) => {
    // Si on passe de filtres à pas de filtres (reset), remonter en haut
    if (newLen === 0 && oldLen > 0) {
      resetScrollPosition()
    }
  },
)

// ===== Redesign V3 — Bloc titre éditorial =====
// REGION_BY_SLUG et DEPT_NAMES remontés plus haut (nécessaires à clusterContextLabels).
// Pull-quote curated dérivé via shared/region-tagline-map.

// Label département courant (ex: "Aveyron") — null si pas de filtre département
const currentDepartmentLabel = computed(() => {
  if (!filters.value.department) return null
  return DEPT_NAMES[filters.value.department]?.name ?? null
})

// Label région courant (ex: "Occitanie") — dérivé de la région filtrée OU de la région du département filtré
const currentRegionLabel = computed(() => {
  if (filters.value.region) return resolveRegion(filters.value.region)?.name ?? null
  if (filters.value.department) return DEPT_NAMES[filters.value.department]?.regionName ?? null
  return null
})

// Slug région utilisé pour getTagline (priorise celui des filtres, sinon dérivé du département)
const currentRegionSlug = computed(() => {
  if (filters.value.region) return resolveRegion(filters.value.region)?.slug ?? null
  if (filters.value.department) return DEPT_NAMES[filters.value.department]?.regionSlug ?? null
  return null
})

// Eyebrow : "OCCITANIE · AVEYRON" / "BRETAGNE · TOUTES DÉPARTEMENTS" / "TOUTES RÉGIONS"
const heroEyebrow = computed(() => {
  if (currentRegionLabel.value && currentDepartmentLabel.value) {
    return t('pages.search.v3.eyebrow', {
      region: currentRegionLabel.value.toUpperCase(),
      department: currentDepartmentLabel.value.toUpperCase(),
    })
  }
  if (currentRegionLabel.value) {
    return t('pages.search.v3.eyebrowRegionOnly', { region: currentRegionLabel.value.toUpperCase() })
  }
  return t('pages.search.v3.eyebrowAllFrance')
})

// H1 généralisé selon le type d'hébergement (spec 4.28.0) :
// - all  → "Chambres d'hôtes et gîtes en …"
// - gite → "Gîtes en …"
// - cdh  → "Chambres d'hôtes en …"
const heroTitleTypeKey = computed<'all' | 'cdh' | 'gite'>(() => {
  if (filters.value.type === 'gite') return 'gite'
  if (filters.value.type === 'chambre-hotes') return 'cdh'
  return 'all'
})
const heroTitle = computed(() => {
  // Département plus spécifique que région
  const location = currentDepartmentLabel.value || currentRegionLabel.value
  const key = heroTitleTypeKey.value
  if (!location) return t(`pages.search.v3.titleByType.${key}.france`)
  return t(`pages.search.v3.titleByType.${key}.located`, { location })
})

// Pull-quote italique curated — null si hors mapping (pas de fallback)
const heroTagline = computed(() =>
  getTagline(
    { department: filters.value.department, region: currentRegionSlug.value },
    locale.value === 'en' ? 'en' : 'fr',
  ),
)

// Note : compteur "X chambres d'hôtes" retiré du sous-titre (trompeur quand l'utilisateur
// zoome sur une zone avec moins de résultats). Le compteur live est désormais dans la
// toolbar carte (HoliSearchMapToolbar) qui distingue total global vs viewport.

// Bandeau "Triés du plus proche au plus éloigné de [lieu]" — visible quand le tri par
// distance est actif (explicite ou implicite via défaut contextuel serveur). Disparaît
// dès que l'utilisateur choisit un autre tri.
// Number.isFinite : protège contre les `?centerLat=abc` qui produisent NaN
// (NaN != null est vrai, mais le serveur rejette en 400).
const hasGeoContext = computed(() =>
  !!filters.value.city
  || !!filters.value.q
  || (Number.isFinite(filters.value.centerLat) && Number.isFinite(filters.value.centerLng)),
)
const isSortedByDistance = computed(() => {
  // sortBy explicite : ne montrer le bandeau que si l'utilisateur a choisi 'distance'
  // ET qu'on a vraiment un point de référence (sinon le serveur retombe sur createdAt).
  if (filters.value.sortBy) {
    return filters.value.sortBy === 'distance' && hasGeoContext.value
  }
  // sortBy implicite : défaut contextuel serveur = distance si geo context
  return hasGeoContext.value
})
const distanceBannerLocation = computed<string | null>(() => {
  // Pas de geo context = pas de point de référence → ne rien afficher comme label
  // (le bandeau lui-même est masqué via isSortedByDistance dans ce cas).
  if (!hasGeoContext.value) return null
  if (filters.value.city) return filters.value.city
  if (filters.value.q) return filters.value.q
  const labelFromQuery = route.query.locationName
  if (typeof labelFromQuery === 'string' && labelFromQuery.trim()) {
    return labelFromQuery.trim().slice(0, 80)
  }
  return null
})

// Dropdown "Affiner par département" — visible uniquement quand une région est filtrée.
// Cohérence avec /regions/[slug] : permet de zoomer rapidement sur un département de la région.
const currentRegionResolved = computed(() => filters.value.region ? resolveRegion(filters.value.region) : null)
const regionDepartmentOptions = computed(() => {
  const region = currentRegionResolved.value
  if (!region) return []
  return region.departments.map(dept => ({
    value: `${dept.code}-${dept.slug}`,
    label: `${dept.name} (${dept.code})`,
  }))
})
const selectedRegionDepartment = ref<string | undefined>(undefined)
watch(selectedRegionDepartment, (codeSlug) => {
  if (!codeSlug) return
  const region = currentRegionResolved.value
  if (!region) return
  const dept = region.departments.find(d => `${d.code}-${d.slug}` === codeSlug)
  if (!dept) return
  // Préserve type, dates et voyageurs lors du saut sur la page département
  navigateTo({ path: localePath(`/${codeSlug}`), query: pseoNavigationQuery(dept.name) })
})

// ===== Réversibilité granulaire (spec 3.20.0) =====
// Chips actifs supprimables individuellement.
// La carte n'est PAS re-fitBoundée sur retrait : la viewport courante est conservée.

// Label éditorial (passé via ?locationName=) utilisé pour personnaliser le chip "Autour de ...".
const chipsLocationName = computed(() => {
  const raw = route.query.locationName
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed.slice(0, 80) : null
})

/**
 * Retire en bloc les clés données du jeu de filtres courant via une seule
 * mutation `updateFilters` (= un seul `router.push`). Préserve toutes les autres
 * clés et reset `page` à 1. La page ne déclenche AUCUN refit/zoom carte —
 * la viewport reste figée selon le viewportSyncMode courant.
 * `patch` (optionnel) est reposé après le retrait : retirer un seul séjour type
 * efface `sejours` puis repose le reste (une valeur `undefined` reste absente).
 */
function removeFilters(keys: (keyof typeof filters.value)[], patch?: Partial<typeof filters.value>) {
  const next: Record<string, unknown> = {}
  const toRemove = new Set<string>(keys.map(String))
  for (const [k, v] of Object.entries(filters.value)) {
    if (toRemove.has(k)) continue
    if (v === undefined || v === '' || v === false) continue
    next[k] = v
  }
  if (patch) {
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue
      next[k] = v
    }
  }
  // Reset page à 1 dès qu'un filtre change
  next.page = 1
  // Cast safe : next contient un sous-ensemble de SearchFilters
  updateFilters(next as typeof filters.value)
}

// Accent de type du bouton de recherche de la bottom-bar mobile : reprend la
// couleur de l'option active du switcher (spec 4.74.1), comme la barre desktop.
const mobileTypeAccentVar = computed(() => searchTypeAccentVar(filters.value.type))
const mobileTypeAccentInkClass = computed(() => searchTypeAccentInkClass(filters.value.type))

/**
 * Change le type d'hébergement (spec 4.28.0). Ne mute QUE `type` : dates,
 * voyageurs, localisation, quick-filters, tri et viewport restent intacts.
 * `all` retire le param (mode « Tous », URL propre). `page` reset à 1.
 */
function handleTypeChange(next: SearchTypeValue) {
  updateFilters(withSearchType(filters.value, next))
}

// L'annonce polie du retrait est portée par HoliSearchActiveChips.
function handleRemoveChip(keys: (keyof typeof filters.value)[], _label?: string, patch?: Partial<typeof filters.value>) {
  removeFilters(keys, patch)
}

function handleClearAllChips() {
  resetFilters()
}

</script>

<template>
  <div class="min-h-screen search-page-v3 bg-paper-50">
    <!-- Filtres -->
    <HoliSearchFilters
      :model-value="filters"
      :show-mobile-filters="showMobileFilters"
      paper-surface
      @update:model-value="updateFilters"
      @update:show-mobile-filters="showMobileFilters = $event"
      @location-label="_currentDeptLabel = $event"
      @navigate-to-slug="(path, query) => navigateTo({ path: localePath(path), query: Object.keys(query).length > 0 ? query : undefined })"
    />

    <!-- Ligne « Vos critères » : une étiquette par critère, pleine largeur sous la barre -->
    <HoliSearchActiveChips
      :filters="filters"
      :location-name="chipsLocationName"
      @remove="handleRemoveChip"
      @clear-all="handleClearAllChips"
    />

    <!-- Contenu principal - Layout split-view -->
    <div class="container mx-auto px-4 py-6 pb-20 md:pb-6">
      <!-- Bandeau d'élargissement automatique du rayon (signal transitoire, hors wrapper unifié) -->
      <div
        v-if="expandedRadiusKm != null && !loading"
        class="mb-3 flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800"
        role="status"
      >
        <UIcon
          name="i-lucide-map-pin"
          class="w-5 h-5 shrink-0"
        />
        <p>
          {{ $t('pages.search.expandedRadius', { radius: expandedRadiusKm }) }}
        </p>
      </div>

      <!-- ===========================================================
           Bandeau contextuel unifié /search (Option β, spec 3.20.2)
           Regroupe : breadcrumb · sort banner · hero éditorial · chips
           Séparé de la grille de résultats par sa propre frontière visuelle.
           =========================================================== -->
      <section
        class="bg-paper-100/60 border border-paper-300/40 rounded-lg px-4 md:px-6 py-4 md:py-5 mb-6"
        aria-labelledby="search-hero-title"
      >
        <!-- Encart 2 colonnes (spec 4.31.0) : breadcrumb à gauche, hero éditorial à
             droite, côte à côte à partir de md (empilé sur mobile). -->
        <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">

          <!-- Colonne gauche : breadcrumb + bandeau tri distance contextuel.
               max-w borné pour qu'un nom de lieu long dans le bandeau ne comprime
               pas la colonne droite (hero). -->
          <div class="min-w-0 md:max-w-xs space-y-3">
            <UBreadcrumb :items="breadcrumbItems" />
            <!-- Switcher de type déplacé dans la barre de recherche (HoliSearchFilters, spec 4.30.0) -->
            <HoliSortByDistanceBanner
              v-if="isSortedByDistance && !loading && establishments.length > 0"
              :location-name="distanceBannerLocation"
            />
          </div>

          <!-- Colonne droite : hero éditorial (eyebrow + H1 + pull-quote + cluster + dropdown dept) -->
          <div class="min-w-0 md:flex-1 md:text-right">
            <div class="tag text-paper-900/60 mb-3">
              {{ heroEyebrow }}
            </div>
            <h1
              id="search-hero-title"
              class="font-display font-light text-[28px] md:text-[32px] leading-[1.05] tracking-[-0.02em] text-paper-900"
            >
              {{ heroTitle }}
            </h1>
            <p
              v-if="heroTagline"
              class="font-display italic text-[15px] md:text-[17px] text-paper-900/70 mt-3 max-w-xl md:ml-auto leading-snug"
            >
              &laquo; {{ heroTagline }} &raquo;
            </p>
            <p
              v-if="loading && establishments.length === 0"
              class="text-[15px] text-paper-900/70 mt-3"
            >
              {{ $t('pages.search.searching') }}
            </p>

            <!-- Sous-titre contextuel "Vue actuelle : <dept> · <région>" -->
            <!-- Mis à jour UNIQUEMENT au clic cluster (intent explicite) — jamais sur pan/zoom. -->
            <!-- Ne mute pas le H1 SEO : signal UI uniquement, reset au Voir tous / dézoom. -->
            <div
              v-if="clusterContextLabels"
              class="tag text-sage-700 mt-4 hidden lg:block"
            >
              {{
                clusterContextLabels.dept && clusterContextLabels.region
                  ? $t('pages.search.v3.viewport.currentViewDeptRegion', { dept: clusterContextLabels.dept.toUpperCase(), region: clusterContextLabels.region.toUpperCase() })
                  : $t('pages.search.v3.viewport.currentViewRegion', { region: clusterContextLabels.region?.toUpperCase() })
              }}
            </div>

            <!-- Dropdown "Affiner par département" (visible uniquement quand une région est filtrée et qu'on n'est pas déjà sur un département).
                 text-left explicite : annule le text-right hérité de la colonne (icône + libellé alignés à gauche). -->
            <USelect
              v-if="regionDepartmentOptions.length > 0 && !filters.department"
              v-model="selectedRegionDepartment"
              :items="regionDepartmentOptions"
              value-key="value"
              :placeholder="$t('pages.regions.show.filterByDepartment')"
              icon="i-lucide-map-pin"
              class="mt-4 w-full md:w-60 md:ml-auto text-left"
            />
          </div>
        </div>

      </section>

      <!-- Layout split-view: liste + carte sur desktop -->
      <div class="lg:grid lg:grid-cols-2 lg:gap-6">
        <!-- Colonne gauche: Liste des resultats -->
        <!-- Toolbar sync viewport ↔ liste est désormais intégrée dans la carte (HoliSearchMapToolbar) -->
        <div class="lg:pr-4">
          <!-- Etat d'erreur -->
          <div
            v-if="error"
            class="text-center py-8"
          >
            <UIcon
              name="i-lucide-alert-circle"
              class="w-12 h-12 text-red-500 mx-auto mb-4"
            />
            <p class="text-red-600 mb-4">
              {{ $t('pages.search.errorMessage') }}
            </p>
            <UButton
              variant="outline"
              @click="search"
            >
              {{ $t('pages.search.retry') }}
            </UButton>
          </div>

          <!-- Etat de chargement initial -->
          <div
            v-else-if="loading && establishments.length === 0"
            class="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <USkeleton
              v-for="i in 6"
              :key="i"
              class="h-80 rounded-xl"
            />
          </div>

          <!-- Mode synced : skeletons pendant auto-load des fiches manquantes -->
          <div
            v-else-if="viewportSyncMode === 'synced' && displayedEstablishments.length === 0 && loading"
            class="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <USkeleton
              v-for="i in 4"
              :key="i"
              class="h-80 rounded-xl"
            />
          </div>

          <!-- Mode synced : zone vide (aucun pin dans la viewport) -->
          <div
            v-else-if="viewportSyncMode === 'synced' && displayedEstablishments.length === 0"
            class="text-center py-12"
          >
            <UIcon
              name="i-lucide-map-pin-off"
              class="w-12 h-12 text-paper-900/30 mx-auto mb-4"
            />
            <p class="text-paper-900/70 mb-4">
              {{ $t('pages.search.v3.viewport.noResultsInArea') }}
            </p>
            <UButton
              variant="outline"
              @click="handleResetViewportSync"
            >
              {{ $t('pages.search.v3.viewport.seeAll') }}
            </UButton>
          </div>

          <!-- Grille de resultats -->
          <div
            v-else-if="displayedEstablishments.length > 0"
            class="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr"
          >
            <HoliPropertyCardV3
              v-for="establishment in displayedEstablishments"
              :key="establishment.id"
              :establishment="establishment"
              :highlighted-id="highlightedEstablishmentId"
              :check-in="filters.checkIn"
              :check-out="filters.checkOut"
              :adults="filters.minAdults"
              :children="filters.minChildren"
              :search-query="buildSearchQuery()"
              @hover="hoveredEstablishmentId = $event"
            />
          </div>

          <!-- Etat vide -->
          <div
            v-else
            class="text-center py-16"
          >
            <UIcon
              name="i-lucide-search-x"
              class="w-16 h-16 text-neutral-300 mx-auto mb-4"
            />
            <h2 class="text-xl font-semibold text-neutral-800 mb-2">
              {{ isLargeGroupSearch ? $t('pages.search.emptyLargeGroupTitle') : $t('pages.search.emptyTitle') }}
            </h2>
            <p class="text-neutral-600 mb-6">
              {{ isLargeGroupSearch && canSuggestGites
                ? $t('pages.search.emptyLargeGroupMessage', { count: requestedGuestTotal })
                : $t('pages.search.emptyMessage') }}
            </p>
            <HoliSejourZeroActions
              v-if="filters.sejours?.length"
              :sejours="filters.sejours"
              @remove="handleRemoveChip"
            />
            <div class="flex flex-wrap items-center justify-center gap-2">
              <UButton
                v-if="isLargeGroupSearch && canSuggestGites"
                class="cursor-pointer"
                @click="switchToGites"
              >
                {{ $t('pages.search.emptyLargeGroupCta') }}
              </UButton>
              <UButton
                variant="outline"
                class="cursor-pointer"
                @click="resetFilters"
              >
                {{ $t('pages.search.resetFilters') }}
              </UButton>
            </div>
          </div>

          <!-- Annonces accessibilité (lecteurs d'écran) -->
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            class="sr-only"
          >
            {{ newResultsAnnouncement }}
          </div>

          <!-- Pagination / Load more — masqué en mode synced (le banner gère le chargement) -->
          <div
            v-if="hasMore && viewportSyncMode !== 'synced'"
            class="flex flex-col items-center gap-3 mt-8"
          >
            <p class="text-sm text-neutral-600">
              {{ $t('pages.search.displayedCount', { shown: establishments.length, total }) }}
            </p>
            <UButton
              :loading="loading"
              size="lg"
              variant="outline"
              @click="loadMore"
            >
              {{ $t('pages.search.loadMore') }}
            </UButton>
          </div>

          <!-- Indicateur de fin -->
          <div
            v-else-if="establishments.length > 0 && !hasMore"
            class="text-center mt-8 text-neutral-500 text-sm"
          >
            {{ $t('pages.search.allResultsSeen') }}
          </div>
        </div>

        <!-- Colonne droite: Carte (sticky sur desktop) -->
        <!-- Hauteur calc : 100vh - barre sticky filtres - marge top 16 - marge bottom 32 -->
        <!-- La toolbar (HoliSearchMapToolbar) est désormais intégrée dans SearchMapV3 -->
        <div class="hidden lg:block lg:sticky lg:top-[calc(var(--search-bar-bottom,110px)+16px)] lg:h-[calc(100vh-var(--search-bar-bottom,110px)-48px)] relative">
          <ClientOnly>
            <HoliSearchMapV3
              ref="desktopMapRef"
              :map-points="mapPoints"
              :active-type="filters.type"
              :highlighted-id="highlightedEstablishmentId"
              :restore-viewport="mapSavedViewport"
              :viewport-sync-mode="viewportSyncMode"
              :loaded="viewportStats.loaded"
              :viewport-total="viewportStats.total"
              :global-total="total"
              :show-search-in-area="showSearchInAreaButton"
              :loading="loading"
              :zoom-up-label="zoomUpLabel"
              @marker-hover="hoveredEstablishmentId = $event"
              @marker-click="scrollToCard"
              @popup-navigate="handlePopupNavigate"
              @restore-consumed="clearSavedViewport"
              @viewport-change="handleViewportChange"
              @cluster-click="handleClusterClick"
              @toolbar-search-in-area="handleSearchInThisArea"
              @toolbar-zoom-up="handleZoomUp"
              @toolbar-reset="handleResetViewportSync"
            />
            <template #fallback>
              <USkeleton class="w-full h-full min-h-[400px] rounded-xl" />
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>

    <!-- Barre bottom mobile : recherche + filtres + carte -->
    <div data-bottom-bar class="md:hidden fixed bottom-0 inset-x-0 z-[1000] bg-paper-100/95 backdrop-blur-sm border-t border-paper-300/40 px-3 py-2 safe-bottom">
      <div class="flex flex-col gap-2">
        <!-- Ligne 0 : Switcher de type (mobile) — pleine largeur, rectangles arrondis (spec 4.30.0) -->
        <HoliSearchTypeSwitcher
          :model-value="filters.type"
          variant="block"
          @update:model-value="handleTypeChange"
        />
        <!-- Ligne 1 : Recherche mobile + bouton Rechercher -->
        <div class="flex items-center gap-2">
          <HoliSearchAutocomplete
            v-model="mobileSearchText"
            :placeholder="$t('common.search.placeholderMobile')"
            size="lg"
            drop="up"
            class="flex-1"
            @select="handleMobileSuggestionSelect"
          />
          <UButton
            icon="i-lucide-search"
            size="lg"
            color="neutral"
            class="type-accent-btn cursor-pointer"
            :class="mobileTypeAccentInkClass"
            :style="{ '--type-accent': mobileTypeAccentVar }"
            :aria-label="$t('common.search.searchButton')"
            @click="applyMobileSearch"
          />
        </div>
        <!-- Ligne 2 : Boutons filtres + carte -->
        <div class="flex items-center gap-2">
          <UButton
            icon="i-lucide-sliders-horizontal"
            variant="outline"
            size="lg"
            class="flex-1"
            @click="openMobileFilters"
          >
            {{ activeFilterCount > 0 ? $t('common.search.filtersWithCount', { count: activeFilterCount }) : $t('common.search.filters') }}
          </UButton>
          <UButton
            icon="i-lucide-map"
            variant="outline"
            size="lg"
            class="flex-1"
            :aria-label="$t('pages.search.viewMap')"
            @click="showMobileMap = true"
          >
            {{ $t('pages.search.viewMap') }}
          </UButton>
        </div>
      </div>
    </div>

    <!-- Bottom sheet carte mobile -->
    <UDrawer
      v-model:open="showMobileMap"
      direction="bottom"
      :ui="{ overlay: 'fixed inset-0 bg-paper-900/60' }"
    >
      <template #header>
        <div class="flex items-center justify-between w-full">
          <span class="font-semibold text-lg">{{ $t('pages.search.mapTitle') }}</span>
          <UButton
            icon="i-lucide-x"
            variant="ghost"
            size="sm"
            :aria-label="$t('common.buttons.close')"
            @click="showMobileMap = false"
          />
        </div>
      </template>
      <template #body>
        <div class="h-[60vh]" data-vaul-no-drag>
          <ClientOnly>
            <HoliSearchMapV3
              :map-points="mapPoints"
              :active-type="filters.type"
              :highlighted-id="highlightedEstablishmentId"
              :restore-viewport="mapSavedViewport"
              :global-total="total"
              @marker-hover="hoveredEstablishmentId = $event"
              @marker-click="scrollToCard"
              @popup-navigate="handlePopupNavigate"
              @restore-consumed="clearSavedViewport"
            />
            <template #fallback>
              <USkeleton class="w-full h-full min-h-[300px] rounded-xl" />
            </template>
          </ClientOnly>
        </div>
      </template>
    </UDrawer>
  </div>
</template>

<style scoped>
/* Mouvement réduit : neutralise toutes les transitions de la page (reprise de
   l'ancienne surcouche CSS de la barre, supprimée en 4.118.0). */
@media (prefers-reduced-motion: reduce) {
  .search-page-v3 :deep(*) {
    transition: none !important;
  }
}
</style>
